import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EnumsService from '../../Services/enums.service';
import SubAccountantService from '../../Services/subAccountant.service';

/**
 * Fetches PaymentAcceptance page enums: account cards, pal codes, check types, client items, sub-accountants.
 * Cached by caseId so repeat visits are fast.
 */
export const fetchPaymentAcceptanceEnums = createAsyncThunk(
    'paymentAcceptanceEnums/fetch',
    async (
        {
            caseId,
            accountantId,
            token,
            addItemLabel = 'Add Item',
            addAccountCardLabel = 'Add Account Card',
            clientName,
            forceRefetch = false,
        },
        { rejectWithValue, getState }
    ) => {
        const cached = getState()?.paymentAcceptanceEnumsSlice?.byCaseId?.[caseId];
        if (cached && !forceRefetch) {
            return { caseId, ...cached };
        }
        try {
            const [
                accountsResponse,
                palCodesResponse,
                checksTypeResponse,
                clientItemsResponse,
                sortCodesResponse,
                subAccountantResult,
                accountantResult,
            ] = await Promise.all([
                EnumsService.GetAccountCardsByCaseId(caseId, token),
                EnumsService.GetPalCodesByAccountantId(accountantId, token),
                EnumsService.GetChecksTypesByAccountantId(accountantId, token),
                EnumsService.GetClientItemsByCaseId(caseId, token),
                EnumsService.GetSortCodesByAccountantId(accountantId, token),
                SubAccountantService.GetSubAccountantByAccountantId(token, accountantId),
                SubAccountantService.GetAccountantById(token, accountantId),
            ]);

            const accountCardData = accountsResponse.ok ? await accountsResponse.json() : [];
            const palCodesData = palCodesResponse.ok ? await palCodesResponse.json() : [];
            const checkTypeOptions = checksTypeResponse.ok ? await checksTypeResponse.json() : [];
            const clientItemsData = clientItemsResponse.ok ? await clientItemsResponse.json() : [];
            const sortCodeIdOptions = sortCodesResponse.ok ? await sortCodesResponse.json() : [];

            const accountCardOptions = [{ name: addAccountCardLabel, value1: 'AddItem', cardId: -1 }, ...accountCardData];
            const accountCardMap = (accountCardData || []).reduce((acc, item) => {
                acc[item.cardId] = item.name;
                return acc;
            }, {});
            const formattedPalCodeOptions = (palCodesData || []).map((palCode) => ({
                label: palCode.palCodeId,
                value: palCode.palCodeId,
            }));
            const clientItemsWithAdd = [{ makat: 'add-item', name: addItemLabel }, ...(clientItemsData || [])];

            const enhancedAccountant = accountantResult
                ? { ...accountantResult, subAccountantId: accountantResult.accountantId }
                : null;
            const combinedSubAccountants = enhancedAccountant ? [enhancedAccountant, ...(subAccountantResult || [])] : subAccountantResult || [];
            if (clientName) {
                combinedSubAccountants.push({ subAccountantId: caseId, name: clientName });
            }
            const subAccountantOptions = combinedSubAccountants.map((acc) => ({
                value: acc.subAccountantId,
                label: acc.name,
            }));

            return {
                caseId,
                accountCardOptions,
                editAccountCardOptions: accountCardData,
                accountCardEditDownloadOptions: accountCardData,
                accountCardMap,
                formattedPalCodeOptions,
                checkTypeOptions,
                sortCodeIdOptions,
                clientItems: clientItemsWithAdd,
                subAccountants: combinedSubAccountants,
                subAccountantOptions,
                accountant: enhancedAccountant,
                subAccountantsError: !subAccountantResult && !accountantResult,
            };
        } catch (error) {
            return rejectWithValue(error?.message || 'Unknown error');
        }
    }
);

const initialState = {
    byCaseId: {},
    loading: {},
    error: {},
};

const paymentAcceptanceEnumsSlice = createSlice({
    name: 'paymentAcceptanceEnums',
    initialState,
    reducers: {
        clearPaymentAcceptanceEnums: (state, action) => {
            const caseId = action.payload;
            if (caseId) {
                delete state.byCaseId[caseId];
                delete state.loading[caseId];
                delete state.error[caseId];
            } else {
                state.byCaseId = {};
                state.loading = {};
                state.error = {};
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPaymentAcceptanceEnums.pending, (state, action) => {
                const caseId = action.meta?.arg?.caseId;
                if (caseId) state.loading[caseId] = true;
                if (caseId) state.error[caseId] = null;
            })
            .addCase(fetchPaymentAcceptanceEnums.fulfilled, (state, action) => {
                const { caseId, ...rest } = action.payload;
                state.byCaseId[caseId] = rest;
                state.loading[caseId] = false;
                state.error[caseId] = null;
            })
            .addCase(fetchPaymentAcceptanceEnums.rejected, (state, action) => {
                const caseId = action.meta?.arg?.caseId;
                if (caseId) {
                    state.loading[caseId] = false;
                    state.error[caseId] = action.payload;
                }
            });
    },
});

export const { clearPaymentAcceptanceEnums } = paymentAcceptanceEnumsSlice.actions;

export const selectPaymentAcceptanceEnums = (state, caseId) =>
    (caseId && state.paymentAcceptanceEnumsSlice?.byCaseId?.[caseId]) || null;

export const selectPaymentAcceptanceEnumsLoading = (state, caseId) =>
    (caseId && state.paymentAcceptanceEnumsSlice?.loading?.[caseId]) || false;

export const selectPaymentAcceptanceEnumsError = (state, caseId) =>
    (caseId && state.paymentAcceptanceEnumsSlice?.error?.[caseId]) || null;

export default paymentAcceptanceEnumsSlice.reducer;
